import { useEffect, useState, useCallback } from "react";
import { api, currency, dateShort, fileUrl } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Bits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, CheckCircle2, PlayCircle, Sparkles, Package } from "lucide-react";
import { toast } from "sonner";

function Confidence({ value }) {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : pct >= 75 ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-red-100 text-red-700 border-red-200";
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${tone}`}>
    {pct >= 100 ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
    Eşleşme %{pct}
  </span>;
}

export default function Matching() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, p] = await Promise.all([api.get("/matching/pending"), api.get("/products")]);
    setOrders(o.data); setProducts(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (order, idx, productId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/assign-product`, { item_index: idx, product_id: productId });
      if (data.all_matched) toast.success("Tüm ürünler eşleşti — sipariş üretim panosuna düştü");
      else toast.success("Ürün eşleştirildi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  const forceStart = async (order) => {
    if (!window.confirm("Reçetesi kontrol edilmeden üretime alınsın mı?")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/start-production`);
      toast.success("Sipariş üretim panosuna düştü");
      load();
    } catch (e) { toast.error("Hata"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-8">
      <PageHeader
        eyebrow="Akıllı Eşleştirme"
        title="Sipariş → Reçete Eşleştirme"
        subtitle="Etsy ve diğer kanallardan gelen siparişleri kendi reçetelerinizle eşleştirin — üretim akışı otomatik başlar"
      />

      {orders.length === 0 ? (
        <EmptyState icon={Sparkles} title="Tüm siparişler eşleşti" description="Bekleyen manuel eşleştirme yok. Yeni sipariş geldiğinde burada listelenir." />
      ) : (
        <div className="space-y-5">
          {orders.map((o) => (
            <Card key={o.id} className="border-slate-200 p-5">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs font-semibold text-slate-500">{o.order_no}</span>
                    {o.source === "etsy" && <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-[10px]">ETSY</Badge>}
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Reçete bekliyor</Badge>
                  </div>
                  <div className="font-display font-bold text-lg text-slate-900">{o.customer?.name || "-"}</div>
                  <div className="text-xs text-slate-500">{dateShort(o.created_at)} · {currency(o.total)}</div>
                </div>
                <Button data-testid="matching-force-start" onClick={() => forceStart(o)} variant="outline" disabled={busy}>
                  <PlayCircle className="h-4 w-4 mr-1" /> Reçetesiz Başlat
                </Button>
              </div>

              <div className="space-y-3">
                {o.items?.map((it, idx) => {
                  const matched = (it.match_confidence || 0) >= 1.0;
                  return (
                    <div key={`${o.id}-${idx}`} className={`flex items-start gap-4 rounded-lg border p-3 ${matched ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/40 border-amber-200"}`}>
                      {it.image_url ? (
                        <img src={fileUrl(it.image_url)} alt="" className="h-16 w-16 rounded-md object-cover border border-slate-200 shrink-0" />
                      ) : (
                        <div className="h-16 w-16 rounded-md bg-slate-100 border border-slate-200 grid place-items-center shrink-0">
                          <Package className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Gelen Ürün</span>
                          <Confidence value={it.match_confidence} />
                          {it.matched_manually && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]">Manuel</Badge>}
                        </div>
                        <div className="font-semibold text-slate-900 truncate">{it.etsy_title || it.product_name}</div>
                        <div className="text-xs text-slate-500 mb-2">{it.qty}× · {currency(it.price)}</div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Reçete Ata:</span>
                          <Select
                            value={it.product_id || ""}
                            onValueChange={(v) => assign(o, idx, v)}
                            disabled={busy}
                          >
                            <SelectTrigger className="h-10 w-64" data-testid={`matching-select-${o.id}-${idx}`}>
                              <SelectValue placeholder="Reçete seç..." />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} {p.bom?.length ? `· ${p.bom.length} malzeme` : "· reçetesiz"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {it.product_name && !matched && (
                            <span className="text-xs text-slate-500">Öneri: <b>{it.product_name}</b></span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
